import { doc, getFirestore, setDoc } from "firebase/firestore";
import { detectFieldType } from "./tableUtils.js";
import { safeStringify, flattenObject, convertTextToObject, sortKeys, convertObjectToText, unflattenObject } from 'cando-util';
import { defaultCollectionCriteria, criteriaFields } from '$lib/utilities/defaultCollections.js';

export const buildAutoTableFields = (records, collectionName) => {
	const docEntries = Object.entries(records || {});
	if (!docEntries.length) return [];
	const autoKeys = Array.from(
		docEntries.reduce((set, [_, record]) => {
			Object.keys(record || {}).forEach((key) => set.add(key));
			return set;
		}, new Set()),
	);
	const headingPrefix = formatHeading(collectionName) || "Record";
	const autoFields = [
		{
			heading: `${headingPrefix} Id`,
			key: "__docId",
			type: undefined,
			isIdentifier: true,
			function: (docId) => docId,
		},
		...autoKeys.map((key) => {
			const dynamicTypeResolver = (record = {}) =>
				detectFieldType(key, record?.[key]);
			return {
				heading: formatHeading(key),
				key,
				dynamicTypeResolver,
				function: getFieldAccessor(key, dynamicTypeResolver, records),
			};
		}),
	];
	const normalizedFields = ensureUniqueHeadings(autoFields);
	return [...normalizedFields].sort((a, b) =>
		a.heading.localeCompare(b.heading, undefined, {
			sensitivity: "base",
		}),
	);
};

export const resolveTableFields = (
	tableFields,
	records,
	collectionName,
) =>
	Array.isArray(tableFields) && tableFields.length
		? tableFields
		: buildAutoTableFields(records, collectionName);

export const formatHeading = (text) => {
	if (!text) return "";
	return text
		.replace(/[_-]+/g, " ")
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/\s+/g, " ")
		.trim()
		.replace(/^[a-z]/, (char) => char.toUpperCase());
};

const getFieldAccessor = (key, typeOrResolver, docs) => (docId) => {
	const record = { __docId: docId, ...docs?.[docId] };
	const resolvedType =
		typeOrResolver && typeof typeOrResolver === "function"
			? typeOrResolver(record)
			: typeOrResolver;
	return formatFieldValue(record, key, resolvedType);
};

const formatFieldValue = (record, key, type) => {
	const rawField = extractFieldValue(record, key);
	if (typeof rawField === "undefined" || rawField === null) return "";
	if (type === "file") {
		return rawField?.value || rawField || {};
	}
	if (type === "Image") {
		const value = rawField?.value || rawField;
		return { [value.name]: value };
	}
	if (type === "TextObject") {
		return safeStringify(rawField?.value ?? rawField);
	}
	if (typeof rawField === "object" && "value" in rawField) {
		return rawField.value ?? "";
	}
	return rawField;
};

const extractFieldValue = (record, key) => {
	if (!record) return undefined;
	if (key === "__docId") return record.__docId;
	return record?.[key];
};

const ensureUniqueHeadings = (fields) => {
	const counts = {};
	return fields.map((field) => {
		const heading = field.heading || "Column";
		const nextCount = (counts[heading] || 0) + 1;
		counts[heading] = nextCount;
		if (nextCount === 1) return field;
		return { ...field, heading: `${heading} (${nextCount})` };
	});
};

export const setDefault = async (
	store,
	col,
	tableDefaultsSettingId = "App Settings",
) => {
	const newTableLayout = flattenObject(
		{
			columnOrder:
				store.userProfile?.collectionCriteria?.[col]?.columnOrder || [],
		},
		col,
		true,
		true,
	);

	const existingTableLayouts = unflattenObject(convertTextToObject(
		store.settings?.docs?.[tableDefaultsSettingId]?.defaultTableLayouts
			?.value || "",
	));
	delete existingTableLayouts?.[col];

	const newTableLayouts = sortKeys({
		...flattenObject(existingTableLayouts, '', true, true),
		...newTableLayout,
	});

	const updates = {
		defaultTableLayouts: {
			type: "TextArea",
			permission: "Admin",
			...store.settings?.docs?.[tableDefaultsSettingId]
				?.defaultTableLayouts,
			value: convertObjectToText(newTableLayouts),
		},
	};

	await setDoc(
		doc(getFirestore(), "settings", tableDefaultsSettingId),
		updates,
		{
			merge: true,
		},
	);
};

export const getDefault = async (
	store,
	col,
	tableDefaultsSettingId = "App Settings",
) => {
	const defaultTableLayouts =
		convertTextToObject(
			store.settings?.docs?.[tableDefaultsSettingId]?.defaultTableLayouts
				?.value || "{}",
		) || {};

	const storedDefaultTableLayout = unflattenObject(
		sortKeys(defaultTableLayouts),
	)?.[col];
	const defaultTableLayout =
		storedDefaultTableLayout ||
		(tableDefaultsSettingId === "App Settings"
			? undefined
			: { columnOrder: [] });

	const profileDocRef = doc(
		getFirestore(),
		"profiles",
		store.userProfile.uid,
	);

	const updates = {
		...store.userProfile,
		collectionCriteria: {
			...store.userProfile.collectionCriteria,
			[col]: {
				...criteriaFields,
				...defaultCollectionCriteria?.[col],
				...store.userProfile?.collectionCriteria?.[col],
				...defaultTableLayout,
			},
		},
	};

	await setDoc(profileDocRef, updates, { merge: false });

};
